'use client';

import { useEffect, useRef, useState } from 'react';

const metrics = [
  {
    value: 45,
    suffix: '%',
    prefix: '+',
    label: 'Increase in Lead Conversion',
    description: 'Businesses using GrowthOS websites close more deals with conversion-focused design and automated lead follow-ups.',
    color: '#1586FF',
    bg: '#EEF4FF',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M3 17L9 11L13 15L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 7H21V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 60,
    suffix: '%',
    prefix: '',
    label: 'Reduction in Manual Work',
    description: 'GrowthOS automates payment reconciliation, verification checks, and lead routing so your team skips the busywork.',
    // Darkened from #22C55E (2.3:1) to meet WCAG AA text contrast on white.
    color: '#15803D',
    bg: '#F0FDF4',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M13 2L4.09 12.11C3.69 12.59 4.02 13.33 4.64 13.33H11.5L10.5 22L19.41 11.89C19.81 11.41 19.48 10.67 18.86 10.67H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 3,
    suffix: 'X',
    prefix: '',
    label: 'Faster Business Operations',
    description: 'One GrowthOS system for your website, payments, and verification APIs — no more switching between disconnected tools.',
    color: '#7C3AED',
    bg: '#F5F3FF',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
        <path d="M12 7V12L15 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 24,
    suffix: '/7',
    prefix: '',
    label: 'Automated Customer Engagement',
    description: 'Your GrowthOS website captures, qualifies, and follows up with leads around the clock — even while your team is offline.',
    // Darkened from #F97316 (2.8:1) to meet WCAG AA large-text contrast on white.
    color: '#EA580C',
    bg: '#FFF7ED',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
];

function useCountUp(target: number, duration = 1800) {
  // Seeded with the real figure, not 0. The server-rendered HTML is what crawlers and
  // no-JS visitors get, and it used to read "0", "0%", "0X" and — for the 24/7 metric,
  // whose suffix is a separate span — a literal "0/7". The count-up resets to zero only
  // once it actually starts, client-side.
  const [count, setCount] = useState(target);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // prefers-reduced-motion: hold the real figure and never start. Same check as
    // hooks/useAOS.ts. Returning here leaves `started` false, so the animation effect
    // below stays inert.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setCount(target);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      // Was 0.5. A card taller than half the viewport can never reach that ratio, which
      // left the metric reading a literal "0" / "0%" / "0/7" for the whole visit. Any
      // intersection is enough to justify counting.
      { threshold: 0 }
    );
    if (ref.current) observer.observe(ref.current);

    // Backstop: if the observer never fires — card never scrolled far enough, IO
    // unavailable, tab restored from bfcache — show the real number rather than zero.
    // A stat stuck on 0 is worse than one that skipped its animation.
    const failsafe = window.setTimeout(() => setStarted(true), 4000);

    return () => {
      observer.disconnect();
      window.clearTimeout(failsafe);
    };
  }, [started, target]);

  useEffect(() => {
    if (!started) return;
    let frame = 0;
    const totalFrames = Math.round(duration / 16);
    const timer = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (frame >= totalFrames) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return { count, ref };
}

function MetricCard({ metric }: { metric: typeof metrics[0] }) {
  const { count, ref } = useCountUp(metric.value);

  return (
    <div
      ref={ref}
      className="relative rounded-2xl border border-[#E4E9F0] p-6 md:p-8 bg-white overflow-hidden group hover:shadow-[0_8px_40px_rgba(0,9,56,0.08)] transition-all duration-300"
    >
      {/* Background glow on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"
        style={{ background: `radial-gradient(circle at top left, ${metric.color}08, transparent 60%)` }}
      />

      {/* Icon */}
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
        style={{ background: metric.bg, color: metric.color }}
      >
        {metric.icon}
      </div>

      {/* Count */}
      <div className="flex items-end gap-1 mb-3">
        {metric.prefix && (
          <span className="font-NeueMontreal text-[36px] md:text-[48px] font-bold leading-none" style={{ color: metric.color }}>
            {metric.prefix}
          </span>
        )}
        <span className="font-NeueMontreal text-[48px] md:text-[64px] font-black leading-none text-[#090D28]">
          {count}
        </span>
        <span className="font-NeueMontreal text-[36px] md:text-[48px] font-bold leading-none mb-1" style={{ color: metric.color }}>
          {metric.suffix}
        </span>
      </div>

      {/* Label */}
      <p className="font-NeueMontreal text-[18px] font-semibold text-[#090D28] leading-snug mb-2">
        {metric.label}
      </p>

      {/* Description */}
      <p className="schibsted-grotesk text-[14px] text-[#090D28]/70 leading-relaxed">
        {metric.description}
      </p>

      {/* Bottom accent line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(to right, ${metric.color}, transparent)` }}
      />
    </div>
  );
}

export default function BusinessOutcomes() {
  return (
    <div className="py-[60px] md:pb-[80px] bg-[#F8FAFF]">
      <div className="px-4 md:container">
        {/* Metric cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {metrics.map((m) => (
            <MetricCard key={m.label} metric={m} />
          ))}
        </div>

      </div>
    </div>
  );
}
